/**
 * 本地战绩兜底。正常情况下战绩页按本机 `playerId` 从服务端永久档案读取；
 * 服务端没档案或网络失败时，才使用这份 localStorage 摘要。
 *
 * `playerId` 本身仍只存在这台浏览器里：清站点数据、换浏览器或换设备会生成新身份，
 * 服务端无法自动判断两个 id 是同一个人（铁律 6）。
 */

import type { RoleId, Side, WinReason } from "@avalon/shared";

const KEY = "avalon.history.v1";
/** 只留最近这些局。手机 localStorage 就几 MB，别当数据库使 */
const MAX = 200;

export interface GameRecord {
  /** 去重用。同一局在刷新、重连后不该被记两次 */
  readonly id: string;
  /** 本机记录的完成时间 */
  readonly at: number;
  readonly roomId: string;
  readonly roomName: string;
  readonly playerCount: number;
  /** 我这一局的角色与**终局时**的阵营（兰斯洛特可能换过边） */
  readonly roleId: RoleId;
  readonly side: Side;
  readonly winner: Side;
  readonly won: boolean;
  readonly reason: WinReason;
}

/** `/api/players/:id` 中战绩页实际需要的最小归档形状 */
export interface ArchivedMatch {
  readonly id: string;
  readonly roomId: string;
  readonly roomName: string;
  readonly finishedAt: number;
  readonly playerCount: number;
  readonly outcome: { readonly winner: string; readonly reason: string };
  readonly seats: readonly {
    readonly playerId: string;
    readonly roleId: string;
    readonly side: string;
    readonly won: boolean;
  }[];
}

/** 把服务端公开归档裁成战绩页沿用的本地摘要形状 */
export const historyFromMatches = (
  matches: readonly ArchivedMatch[],
  playerId: string,
): GameRecord[] =>
  matches.flatMap((match) => {
    const me = match.seats.find((seat) => seat.playerId === playerId);
    if (!me) return [];
    return [
      {
        id: match.id,
        at: match.finishedAt,
        roomId: match.roomId,
        roomName: match.roomName,
        playerCount: match.playerCount,
        roleId: me.roleId as RoleId,
        side: me.side as Side,
        winner: match.outcome.winner as Side,
        won: me.won,
        reason: match.outcome.reason as WinReason,
      },
    ];
  });

const read = (): GameRecord[] => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as GameRecord[]) : [];
  } catch {
    // 存坏了就当没有，别让一条脏数据把整个大厅打不开
    return [];
  }
};

const write = (records: readonly GameRecord[]): void => {
  try {
    localStorage.setItem(KEY, JSON.stringify(records.slice(0, MAX)));
  } catch {
    // 配额满了就算了，战绩不值得为它报错
  }
};

export const loadHistory = (): readonly GameRecord[] => read();

export const clearHistory = (): void => {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* 同上 */
  }
};

/**
 * 记一局。**同一局重复调用是安全的** ——
 * 刷新页面、断线重连都会让客户端再次进入终局状态，
 * 不去重的话一局会被记好几次，胜率就成了假的。
 *
 * id 里带上 reveal（这一局的发牌结果）：同一个房间连打两局，
 * 房间码和结局原因都可能一样，但发到每个座位上的角色几乎不可能一样。
 */
export const recordGame = (r: Omit<GameRecord, "id" | "at"> & { readonly deal: string }): void => {
  const id = `${r.roomId}:${r.deal}:${r.reason}`;
  const records = read();
  if (records.some((x) => x.id === id)) return;

  const { deal: _deal, ...rest } = r;
  write([{ ...rest, id, at: Date.now() }, ...records]);
};

export interface HistoryStats {
  readonly total: number;
  readonly won: number;
  readonly asBlue: { readonly total: number; readonly won: number };
  readonly asRed: { readonly total: number; readonly won: number };
  /** 每个角色打过几局、赢过几局，按场次多的排前面 */
  readonly byRole: readonly { readonly roleId: RoleId; readonly total: number; readonly won: number }[];
}

export const summarize = (records: readonly GameRecord[]): HistoryStats => {
  const roles = new Map<RoleId, { total: number; won: number }>();
  let won = 0;
  const asBlue = { total: 0, won: 0 };
  const asRed = { total: 0, won: 0 };

  for (const r of records) {
    if (r.won) won += 1;
    const bucket = r.side === "BLUE" ? asBlue : asRed;
    bucket.total += 1;
    if (r.won) bucket.won += 1;

    const cur = roles.get(r.roleId) ?? { total: 0, won: 0 };
    cur.total += 1;
    if (r.won) cur.won += 1;
    roles.set(r.roleId, cur);
  }

  return {
    total: records.length,
    won,
    asBlue,
    asRed,
    byRole: [...roles]
      .map(([roleId, v]) => ({ roleId, ...v }))
      .sort((a, b) => b.total - a.total),
  };
};

/** 胜率，给人看的百分数。0 场时返回 null —— 「0%」会被误读成「打了很多但一场没赢」 */
export const winRate = (total: number, won: number): number | null =>
  total === 0 ? null : Math.round((won / total) * 100);
